import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { accountingKeys } from '@/api/queries/accounting';

// ─────────────────────────────────────────────────────────────
// Système Minimal de Trésorerie (SMT — AUDCIF art. 13), P1.5.
//
// Endpoints (montés sous /api/accounting) :
//   GET   /settings/accounting-system    système comptable du tenant
//   PATCH /settings/accounting-system    bascule NORMAL ⇄ SMT (avertissement
//                                         NON bloquant si écritures normales)
//   POST  /smt/entries                   saisie recette/dépense → UNE écriture
//                                         validée au journal CA (espèces) / BQ (banque)
//   GET   /smt/statements?year=YYYY       états recettes-dépenses + situation
//
// Le « journal du mois » réutilise useJournalEntries (accounting.ts) filtré
// sur la période et les journaux de trésorerie CA/BQ — aucun endpoint dédié.
//
// ⚠ GET /smt/statements renvoie l'objet SmtStatements DIRECTEMENT
// (success(statements)), sans enveloppe nommée — apiClient déballe `data`
// vers l'objet lui-même. Les autres routes SMT enveloppent ({ accountingSystem },
// { entry }).
// ─────────────────────────────────────────────────────────────

export type AccountingSystem = 'NORMAL' | 'SMT' | 'SYCEBNL';
/** Profil d'entité SYCEBNL (spec verticale ONG §2.1) — SYCEBNL uniquement. */
export type EntityProfile = 'ASSOCIATION' | 'PROJET';
export type SmtEntryType = 'RECETTE' | 'DEPENSE';
export type SmtPaymentMode = 'ESPECES' | 'BANQUE';

export interface SmtEntryInput {
  type: SmtEntryType;
  /** YYYY-MM-DD */
  date: string;
  amount: number;
  label: string;
  mode: SmtPaymentMode;
  /** Compte de produit (7xx, recette) ou de charge (6xx, dépense). */
  categorie: string;
}

export interface AccountingSystemResponse {
  accountingSystem: AccountingSystem;
  /** Profil d'entité SYCEBNL (ASSOCIATION | PROJET) — null hors régime SYCEBNL. */
  entityProfile: EntityProfile | null;
  /**
   * « Mode public » (palier B2) — visa du contrôleur financier,
   * ordonnancement, E3/E5. Fait de TENANT, INDÉPENDANT du régime comptable.
   */
  publicExecution: boolean;
}

export interface SetAccountingSystemInput {
  system: AccountingSystem;
  /** Pertinent uniquement avec system = SYCEBNL (validé côté serveur). */
  entityProfile?: EntityProfile | null;
  /** Omis = inchangé (le serveur ne réécrit pas ce qu'on ne lui donne pas). */
  publicExecution?: boolean;
  /**
   * Le MOTIF ÉCRIT de la bascule du mode public (palier B2, Task 5 ;
   * LOLF art. 90). EXIGÉ par le serveur pour SORTIR du mode public :
   * éteindre le mode public retire le contrôleur financier de la chaîne
   * de la dépense, et cet acte est inscrit à la piste d'audit.
   */
  publicExecutionReason?: string;
}

export interface SetAccountingSystemResult {
  accountingSystem: AccountingSystem;
  entityProfile: EntityProfile | null;
  publicExecution: boolean;
  /** Avertissement NON bloquant (passage NORMAL → SMT avec antécédents). */
  warning?: string;
}

export interface SmtStatementLine {
  categorie: string;
  label: string;
  montant: number;
}

export interface SmtSituationSimplifiee {
  tresorerie: number;
  creances: number;
  dettes: number;
  resultat: number;
}

export interface SmtStatements {
  year: number;
  recettes: SmtStatementLine[];
  depenses: SmtStatementLine[];
  totalRecettes: number;
  totalDepenses: number;
  /** Résultat = Σ recettes − Σ dépenses. */
  resultat: number;
  /** Solde de trésorerie (comptes 52x banque + 57x caisse). */
  soldeTresorerie: number;
  situationSimplifiee: SmtSituationSimplifiee;
}

// ─────────────────────────────────────────────────────────────
// Catégories courtes (micro-entreprise) — comptes SYSCOHADA
// POSTABLES vérifiés dans la source du plan comptable
// (packages/accounting/src/chart/syscohada.ts : tout compte ≥ 3
// caractères est imputable). Ces raccourcis servent de repli quand le
// plan comptable du tenant n'est pas encore semé ; sinon la liste réelle
// des comptes imputables (useAccounts) fait foi.
// ─────────────────────────────────────────────────────────────

export interface SmtCategoryShortcut {
  number: string;
  /** Libellé court, orienté micro-entreprise. */
  label: string;
}

export const SMT_RECETTE_SHORTCUTS: readonly SmtCategoryShortcut[] = [
  { number: '701', label: 'Ventes de marchandises' },
  { number: '706', label: 'Prestations de services' },
  { number: '707', label: 'Produits accessoires' },
];

export const SMT_DEPENSE_SHORTCUTS: readonly SmtCategoryShortcut[] = [
  { number: '601', label: 'Achats de marchandises' },
  { number: '605', label: 'Autres achats' },
  { number: '622', label: 'Loyers et charges' },
  { number: '624', label: 'Entretien et réparations' },
  { number: '627', label: 'Publicité et communication' },
  { number: '641', label: 'Impôts et taxes' },
  { number: '661', label: 'Salaires' },
];

export const ACCOUNTING_SYSTEM_LABELS: Record<AccountingSystem, string> = {
  NORMAL: 'Système normal (comptabilité complète)',
  SMT: 'Système Minimal de Trésorerie (SMT)',
  SYCEBNL: 'SYCEBNL (entités à but non lucratif — ONG, associations, projets)',
};

export const ENTITY_PROFILE_LABELS: Record<EntityProfile, string> = {
  ASSOCIATION: 'Association (bilan, compte de résultat en liste, TFT, notes)',
  PROJET: 'Projet de développement (tableau emplois-ressources par bailleur)',
};

// ─────────────────────────────────────────────────────────────
// Clés de requête
// ─────────────────────────────────────────────────────────────

export const smtKeys = {
  all: ['smt'] as const,
  system: () => [...smtKeys.all, 'accounting-system'] as const,
  statements: (year: number) => [...smtKeys.all, 'statements', year] as const,
};

// ─────────────────────────────────────────────────────────────
// Fonctions API
// ─────────────────────────────────────────────────────────────

async function fetchAccountingSystem(): Promise<AccountingSystemResponse> {
  return apiClient.get<AccountingSystemResponse>(
    '/accounting/settings/accounting-system'
  );
}

async function setAccountingSystem(
  input: SetAccountingSystemInput
): Promise<SetAccountingSystemResult> {
  return apiClient.patch<SetAccountingSystemResult>(
    '/accounting/settings/accounting-system',
    input
  );
}

async function recordSmtEntry(input: SmtEntryInput): Promise<void> {
  await apiClient.post('/accounting/smt/entries', input);
}

async function fetchSmtStatements(year: number): Promise<SmtStatements> {
  // success(statements) → apiClient déballe vers SmtStatements directement.
  return apiClient.get<SmtStatements>('/accounting/smt/statements', { year });
}

// ─────────────────────────────────────────────────────────────
// Requêtes
// ─────────────────────────────────────────────────────────────

/** Système comptable courant — GET /accounting/settings/accounting-system (accounting:read) */
export function useAccountingSystem(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: smtKeys.system(),
    queryFn: fetchAccountingSystem,
    staleTime: 1000 * 60,
    enabled: options?.enabled ?? true,
  });
}

/** États SMT de l'exercice — GET /accounting/smt/statements?year= (accounting:read) */
export function useSmtStatements(year: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: smtKeys.statements(year),
    queryFn: () => fetchSmtStatements(year),
    enabled: options?.enabled ?? true,
  });
}

// ─────────────────────────────────────────────────────────────
// Mutations
// ─────────────────────────────────────────────────────────────

/** Bascule NORMAL ⇄ SMT ⇄ SYCEBNL — PATCH /accounting/settings/accounting-system (accounting:settings) */
export function useSetAccountingSystem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SetAccountingSystemInput) => setAccountingSystem(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: smtKeys.system() });
      // Le changement de régime conditionne le plan de comptes semé et
      // le menu (liasse eSINTAX masquée en SYCEBNL) — tout rafraîchir.
      queryClient.invalidateQueries({ queryKey: accountingKeys.all });
    },
  });
}

/** Saisie recette/dépense — POST /accounting/smt/entries (accounting:post) */
export function useRecordSmtEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SmtEntryInput) => recordSmtEntry(input),
    onSuccess: () => {
      // États SMT + journal du mois (écritures) rafraîchis.
      queryClient.invalidateQueries({ queryKey: smtKeys.all });
      queryClient.invalidateQueries({ queryKey: accountingKeys.entries() });
    },
  });
}
